import { URLS } from '@/lib/constants';
import { logger } from '@/lib/logger';
import React, { useEffect, useRef } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { LessonPlayerProps, LessonPlayerRef } from './types';

// Same UA string mobile Safari sends on the web app, where Soundslice video
// is reliable; keep the "iPhone" token (see the userAgent prop below).
const IPHONE_USER_AGENT =
  'Mozilla/5.0 (iPhone; CPU iPhone OS 26_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/26.0 Mobile/15E148 Safari/604.1';

let WebViewComponent: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  WebViewComponent = require('react-native-webview').WebView;
} catch (e) {
  logger.error('react-native-webview could not be loaded.', e);
}

const INJECTED_SCRIPT = `
(function(){
  function postToRN(data) {
    try { window.ReactNativeWebView.postMessage(JSON.stringify(data)); } catch(e){}
  }
  function logToRN(message) {
    postToRN({ type: 'LOG', message: String(message) });
  }

  logToRN('Injected script has loaded.');

  // Disable WebKit double-tap-to-zoom. Double-tapping the video to seek 10s was
  // also triggering the browser's auto-zoom. 'touch-action: manipulation' kills
  // double-tap zoom while keeping pinch zoom and the player's own double-tap
  // gesture intact.
  function disableDoubleTapZoom() {
    try {
      var style = document.createElement('style');
      style.setAttribute('data-no-dbltap-zoom', '1');
      style.innerHTML = 'html,body{touch-action:manipulation !important;}';
      (document.head || document.documentElement).appendChild(style);
    } catch (e) { logToRN('disableDoubleTapZoom failed: ' + e.message); }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', disableDoubleTapZoom);
  } else {
    disableDoubleTapZoom();
  }

  const origin = 'https://www.soundslice.com';
  let notationCheckInterval = null;
  let notationCheckComplete = false;
  let notationZeroStreak = 0;
  var ZERO_DIM_STREAK_MAX = 12;

  const seekQueue = { desiredTime: null };
  const getVideo = () => document.querySelector('video');

  function applySeek(seconds) {
    const video = getVideo();
    if (!video) return false;
    const executeSeek = () => {
      try {
        const wasPaused = video.paused;
        if (wasPaused) video.play();
        video.currentTime = Number(seconds || 0);
        if (wasPaused) video.pause();
        postToRN({ type: 'LOG', message: 'Seek successful to ' + video.currentTime });
      } catch (e) {
        logToRN('Seek error: ' + e.toString());
      }
    };
    if (video.readyState > 1) { executeSeek(); return true; }
    const onCanPlay = () => { video.removeEventListener('canplay', onCanPlay); executeSeek(); };
    video.addEventListener('canplay', onCanPlay);
    setTimeout(executeSeek, 750);
    return true;
  }

  function pumpSeekQueue() {
    if (seekQueue.desiredTime !== null) {
      if (applySeek(seekQueue.desiredTime)) seekQueue.desiredTime = null;
    }
  }
  setInterval(pumpSeekQueue, 250);

  let isReady = false;
  const signalReady = () => {
    if (isReady) return;
    isReady = true;
    postToRN({ type: 'READY' });
    logToRN('Player is ready, sent READY signal to RN.');
  };

  function startNotationPolling() {
    if (notationCheckComplete) return;
    if (notationCheckInterval) clearInterval(notationCheckInterval);
    notationCheckInterval = setInterval(function() {
      window.postMessage(JSON.stringify({ method: 'getNotationDimensions' }), origin);
    }, 750);
  }

  window.addEventListener('message', (event) => {
    if (event.origin !== origin) return;
    try {
      const data = JSON.parse(event.data);
      if (data.method === 'ssCurrentTime') postToRN({ type: 'TIME', currentTime: data.arg });
      if (data.method === 'ssReady' || data.method === 'ssPlayerReady') { signalReady(); startNotationPolling(); }
      if (data.method === 'ssNotationLoaded') {
        notationZeroStreak = 0;
        window.postMessage(JSON.stringify({ method: 'getNotationDimensions' }), origin);
      }
      if (data.method === 'ssNotationDimensions') {
        var w = data.arg.width, h = data.arg.height;
        var hasDims = (w > 0 || h > 0);
        if (hasDims) {
          notationZeroStreak = 0;
          clearInterval(notationCheckInterval);
          notationCheckComplete = true;
          postToRN({ type: 'NOTATION_STATUS', hasNotation: true });
        } else {
          notationZeroStreak++;
          if (notationZeroStreak >= ZERO_DIM_STREAK_MAX) {
            clearInterval(notationCheckInterval);
            notationCheckComplete = true;
            postToRN({ type: 'NOTATION_STATUS', hasNotation: false });
          }
        }
      }
      if (data.method === 'ssNotationVisibility') postToRN({ type: 'NOTATION_VISIBILITY_CHANGED', isVisible: Boolean(data.arg) });
      if (data.method === 'ssPlay')               postToRN({ type: 'TRACKING_PLAY', playhead: data.arg });
      if (data.method === 'ssPause')              postToRN({ type: 'TRACKING_PAUSE' });
      if (data.method === 'ssSeek')               postToRN({ type: 'TRACKING_SEEK', playhead: data.arg });
      if (data.method === 'ssLoopChange')         postToRN({ type: 'TRACKING_LOOP', loopInfo: data.arg });
      if (data.method === 'ssAudioSourceChanged') postToRN({ type: 'TRACKING_AUDIO_SOURCE', recordingId: data.arg });
      if (data.method === 'ssSpeed')              postToRN({ type: 'TRACKING_SPEED', speed: data.arg });
    } catch (e) {
      logToRN('Error in message listener: ' + e.message);
    }
  });

  setInterval(() => {
    try { window.postMessage(JSON.stringify({ method: 'getCurrentTime' }), origin); } catch(e) {}
    const v = getVideo();
    if (v && v.currentTime) postToRN({ type: 'TIME', currentTime: v.currentTime });
  }, 2000);

  setTimeout(signalReady, 2500);

  window.__RN_HANDLE = function(cmd) {
    if (!cmd || !cmd.type) return;
    if (cmd.type === 'CHECK_NOTATION')   { startNotationPolling(); }
    if (cmd.type === 'SEEK')             { seekQueue.desiredTime = Number(cmd.seconds || 0); pumpSeekQueue(); }
    if (cmd.type === 'PAUSE')            { const v = getVideo(); if (v) v.pause(); }
    if (cmd.type === 'PLAY')             { const v = getVideo(); if (v) v.play(); }
    if (cmd.type === 'TOGGLE_PLAY_PAUSE'){ const v = getVideo(); if (v) { if (v.paused) v.play(); else v.pause(); } }
      if (cmd.type === 'SET_NOTATION_MODE') {
      const post = (msg) => window.postMessage(JSON.stringify(msg), origin);
      if (cmd.mode === 'video-only') {
        post({ method: 'setNotationVisibility', arg: false });
      } else {
        post({ method: 'setNotationVisibility', arg: true });
        // arg must be an integer for Soundslice: 1 = automatic (side video when
        // wide / landscape, top video when narrow / portrait), 2 = left, 3 =
        // top. Passing a string (e.g. 'top') is ignored, and any manual drag the
        // user did locks the layout — so we force automatic to keep notation on
        // the side in landscape and on top in portrait.
        post({ method: 'setVideoPosition', arg: 1 });
      }
    }
  };
})(); true;
`;

const LessonPlayerNative = React.forwardRef<LessonPlayerRef, LessonPlayerProps>(
  (
    {
      embedUrl,
      startSeconds,
      notationMode,
      error,
      onReady,
      onTimeUpdate,
      onNotationCheck,
      onNotationVisibilityChange,
      onNativeTrackingEvent,
      onShouldStartLoad,
    },
    ref
  ) => {
    const webViewRef = useRef<any>(null);

    const notationModeRef = useRef(notationMode);
    useEffect(() => { notationModeRef.current = notationMode; }, [notationMode]);

    const send = (cmd: object) => {
      const js = `window.__RN_HANDLE(${JSON.stringify(cmd)}); true;`;
      webViewRef.current?.injectJavaScript(js);
    };

    React.useImperativeHandle(ref, () => ({
      seekTo: (seconds) => send({ type: 'SEEK', seconds }),
      pause: () => send({ type: 'PAUSE' }),
      play: () => send({ type: 'PLAY' }),
      togglePlayPause: () => send({ type: 'TOGGLE_PLAY_PAUSE' }),
      toggleFullscreen: () => { /* not supported in WebView */ },
      setPlayerView: (mode) => send({ type: 'SET_NOTATION_MODE', mode }),
    }));

    if (!WebViewComponent) {
      return (
        <View style={styles.unavailable}>
          <Text style={styles.unavailableText}>Video player not available</Text>
        </View>
      );
    }

    const handleMessage = (event: any) => {
      try {
        const data = JSON.parse(event.nativeEvent.data);

        if (data?.type === 'LOG') {
          __DEV__ && logger.debug('WebView LOG:', data.message);
          return;
        }

        if (data?.type === 'READY') {
          __DEV__ && logger.debug('WebView is READY.');
          onReady();
          setTimeout(() => send({ type: 'CHECK_NOTATION' }), 250);
          setTimeout(() => send({ type: 'SEEK', seconds: startSeconds }), 500);
          return;
        }

        if (data?.type === 'NOTATION_STATUS') {
          __DEV__ && logger.debug('Received notation status:', data.hasNotation);
          onNotationCheck(data.hasNotation);
          const mode = data.hasNotation ? notationModeRef.current : 'video-only';
          send({ type: 'SET_NOTATION_MODE', mode });
          return;
        }

        if (data?.type === 'NOTATION_VISIBILITY_CHANGED') {
          onNotationVisibilityChange?.(Boolean(data.isVisible));
          return;
        }

        if (data?.type === 'TIME') {
          onTimeUpdate(Number(data.currentTime) || 0);
          return;
        }

        if (data?.type === 'TRACKING_PLAY')         onNativeTrackingEvent('TRACKING_PLAY',         { playhead: data.playhead });
        if (data?.type === 'TRACKING_PAUSE')        onNativeTrackingEvent('TRACKING_PAUSE');
        if (data?.type === 'TRACKING_SEEK')         onNativeTrackingEvent('TRACKING_SEEK',         { playhead: data.playhead });
        if (data?.type === 'TRACKING_LOOP')         onNativeTrackingEvent('TRACKING_LOOP',         { loopInfo: data.loopInfo });
        if (data?.type === 'TRACKING_AUDIO_SOURCE') onNativeTrackingEvent('TRACKING_AUDIO_SOURCE', { recordingId: data.recordingId });
        if (data?.type === 'TRACKING_SPEED')        onNativeTrackingEvent('TRACKING_SPEED',        { speed: data.speed });

      } catch (err) {
        logger.error('Failed to process WebView message:', { error: err });
      }
    };

    return (
      <WebViewComponent
        ref={webViewRef}
        source={{ uri: embedUrl, headers: { Referer: URLS.WORDPRESS_BASE } }}
        style={StyleSheet.absoluteFill}
        injectedJavaScriptBeforeContentLoaded={INJECTED_SCRIPT}
        onShouldStartLoadWithRequest={onShouldStartLoad}
        onMessage={handleMessage}
        javaScriptEnabled
        allowsFullscreenVideo
        allowsInlineMediaPlayback
        // A real iPhone UA makes Soundslice hand the HLS stream to iOS's native
        // player (its check is `canPlayType('application/vnd.apple.mpegurl') &&
        // /i(Phone|Pad)/.test(userAgent)`). With the previous desktop Chrome UA
        // it loaded hls.js + MediaSource inside WKWebView, which intermittently
        // stalls with decode errors (black video, unresponsive play) — see
        // 2026-09-25 device logs. Desktop layout is kept via `fs=1` in the URL.
        userAgent={IPHONE_USER_AGENT}
        mixedContentMode="always"
      />
    );
  }
);

LessonPlayerNative.displayName = 'LessonPlayerNative';
export default React.memo(LessonPlayerNative);

const styles = StyleSheet.create({
  unavailable: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#C9C5C3',
  },
  unavailableText: { color: '#fff', fontSize: 16 },
});
